import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { getApiUrl } from '../utils/apiConfig';

const SuccessContainer = styled.div`
  min-height: 100vh;
  padding-top: clamp(90px, 8vw, 120px);
  background: ${props => props.theme.colors.backgroundLight};
  padding-left: 1rem;
  padding-right: 1rem;
  padding-bottom: 3rem;
  display: flex;
  justify-content: center;
`;

const SuccessCard = styled(motion.div)`
  background: white;
  border-radius: 20px;
  padding: 2rem;
  text-align: left;
  max-width: 900px;
  margin: 0 auto;
  width: 100%;
  box-shadow: ${props => props.theme.shadows.card};
`;

const SuccessIcon = styled(motion.div)`
  .checkmark {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: ${props => props.theme.colors.success};
    color: white;
    font-size: 1.75rem;
  }
`;

const SuccessTitle = styled.h1`
  font-size: 2rem;
  color: ${props => props.theme.colors.text};
  margin: 0 0 .5rem 0;
  font-weight: 700;
`;

const SuccessMessage = styled.p`
  font-size: 1rem;
  color: ${props => props.theme.colors.textLight};
  margin: .25rem 0 1.25rem;
  line-height: 1.7;
`;

const SessionDetails = styled.div`
  background: ${props => props.theme.colors.backgroundLight};
  padding: 1rem 1.25rem;
  border-radius: 12px;
  margin: 1.25rem 0;
  text-align: left;
  
  h3 {
    margin-bottom: .75rem;
    color: ${props => props.theme.colors.text};
    font-size: 1.1rem;
  }
  
  p {
    margin-bottom: 0.4rem;
    color: ${props => props.theme.colors.textLight};
    
    strong {
      color: ${props => props.theme.colors.text};
    }
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: .75rem;
  justify-content: flex-start;
  flex-wrap: wrap;
  margin-top: 1.25rem;
`;

const ActionButton = styled(Link)`
  display: inline-block;
  padding: .75rem 1.25rem;
  border-radius: 10px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
  
  &.btn-primary {
    background: linear-gradient(45deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.secondary});
    color: white;
    box-shadow: 0 5px 15px rgba(46, 204, 113, 0.25);
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(46, 204, 113, 0.45);
    }
  }
  
  &.btn-outline {
    background: transparent;
    border: 2px solid ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.primary};
    
    &:hover {
      background: ${props => props.theme.colors.primary};
      color: white;
    }
  }
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 40px;
  height: 40px;
  border: 4px solid ${props => props.theme.colors.lightGray};
  border-top: 4px solid ${props => props.theme.colors.primary};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 2rem auto;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const Success = () => {
  const [searchParams] = useSearchParams();
  const [sessionData, setSessionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      fetchSessionData();
    } else {
      setLoading(false);
    }
  }, [sessionId]);

  const fetchSessionData = async () => {
    try {
      // API Basis über apiConfig
      const response = await fetch(getApiUrl(`/stripe/session/${sessionId}`));
      if (!response.ok) {
        throw new Error('Fehler beim Laden der Session-Daten');
      }
      const data = await response.json();
      setSessionData(data);
      // Versuche, die Buchung serverseitig zu erzeugen (und Ergebnis anzeigen)
      try {
        const confirmRes = await fetch(getApiUrl(`/stripe/session/${sessionId}/confirm`));
        const confirmData = await confirmRes.json();
        if (confirmData?.success && confirmData?.data?.booking) {
          // Erfolgshinweis unter die Details schreiben
          setSessionData(prev => ({ ...prev, booking: confirmData.data.booking }));
        }
      } catch(_) {}
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (amount, currency) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: currency.toUpperCase()
    }).format(amount / 100);
  };

  return (
    <SuccessContainer>
      <Helmet>
        <title>Zahlung erfolgreich - Der Ponystall</title>
        <meta name="description" content="Ihre Zahlung war erfolgreich. Wir melden uns zeitnah für die Terminabsprache." />
      </Helmet>

      <SuccessCard initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '6px' }}>
          <SuccessIcon initial={{ scale: 0.9 }} animate={{ scale: 1 }} transition={{ duration: 0.3 }}>
            <div className="checkmark">✓</div>
          </SuccessIcon>
          <SuccessTitle>Zahlung erfolgreich</SuccessTitle>
        </div>

        <SuccessMessage>
          Vielen Dank! Ihre Zahlung wurde von Stripe bestätigt. Die Buchung wurde gespeichert.
        </SuccessMessage>

        {loading && sessionId && (
          <LoadingSpinner />
        )}

        {error && (
          <div style={{ 
            background: '#ffebee', 
            color: '#c62828', 
            padding: '1rem', 
            borderRadius: '10px', 
            margin: '1rem 0' 
          }}>
            {error}
          </div>
        )}

        {sessionData && (
          <SessionDetails>
            <h3>Buchungsdetails</h3>
            <p><strong>Betrag:</strong> {formatPrice(sessionData.amount_total, sessionData.currency)}</p>
            {sessionData.customer_email && (
              <p><strong>E-Mail:</strong> {sessionData.customer_email}</p>
            )}
            {sessionData.customer_name && (
              <p><strong>Name:</strong> {sessionData.customer_name}</p>
            )}
            <p><strong>Zahlungsstatus (Stripe):</strong> {sessionData.payment_status === 'paid' ? 'Bezahlt' : sessionData.payment_status}</p>
            <p><strong>Session ID:</strong> {sessionData.id}</p>
            {sessionData.booking && (
              <div style={{ marginTop: '1rem', padding: '0.75rem', background: '#ecfdf5', color: '#065f46', borderRadius: '8px' }}>
                Buchung wurde gespeichert. Buchungs-ID: <strong>{sessionData.booking.id}</strong>
              </div>
            )}
          </SessionDetails>
        )}

        <ActionButtons>
          <ActionButton to="/" className="btn-primary">
            Zur Startseite
          </ActionButton>
          <ActionButton to="/kurse" className="btn-outline">
            Weitere Buchung
          </ActionButton>
        </ActionButtons>
      </SuccessCard>
    </SuccessContainer>
  );
};

export default Success;